import { motion } from "framer-motion";
import { Truck, ShieldCheck, Leaf, RotateCcw, Star } from "lucide-react";

const FEATURES = [
  { icon: Truck, title: "Free AU Shipping", text: "Complimentary express shipping on all orders over $150, Australia-wide." },
  { icon: ShieldCheck, title: "12-Month Warranty", text: "Every device is covered by a full replacement warranty for complete peace of mind." },
  { icon: RotateCcw, title: "30-Day Returns", text: "Try it in your routine. If it isn't for you, returns are simple and free." },
  { icon: Leaf, title: "Considered Materials", text: "Premium, skin-safe and sustainably sourced materials across the range." },
];

const TESTIMONIALS = [
  {
    quote: "I've had neck pain for years and honestly didn't expect much at this price. Two weeks on the pillow and I'm waking up without that stiff, locked-up feeling. Takes a couple of nights to get used to the shape, but now I can't sleep on a normal pillow.",
    name: "Sarah M.",
    location: "Melbourne",
    product: "Butterfly Contour Memory Pillow",
    rating: 5,
    image: "/images/reviews/review-pillow.png",
  },
  {
    quote: "Use the massage gun after every gym session now. The 99 speeds sounded like a gimmick but I actually use the lower ones for warm-up and crank it up on my quads after leg day. Battery's lasted me nearly two weeks. Only wish it came with a case.",
    name: "Daniel K.",
    location: "Sydney",
    product: "Apex 11-in-1 Fascia Massage Gun",
    rating: 4,
    image: "/images/reviews/review-gun.png",
  },
  {
    quote: "Bought the warming belt for period cramps and it's been a lifesaver. Heats up in under a minute and it's quiet enough to wear under a jumper at my desk. So much better than refilling a hot water bottle every hour.",
    name: "Priya R.",
    location: "Brisbane",
    product: "Luna Warming Relief Belt",
    rating: 5,
    image: "/images/reviews/review-belt.png",
  },
];

const TrustSection = () => {
  return (
    <>
      <section id="why" data-testid="trust-section" className="py-24 lg:py-32 bg-white/60 backdrop-blur-sm border-y border-gold/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs tracking-[0.4em] uppercase text-gold mb-4">Why Altius</p>
          <h2 className="font-serif-lux text-4xl sm:text-5xl text-charcoal mb-16">Considered in every detail</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                data-testid={`trust-feature-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <f.icon size={28} strokeWidth={1.25} className="text-gold mb-5" />
                <h3 className="font-serif-lux text-2xl text-charcoal mb-3">{f.title}</h3>
                <p className="text-sm text-charcoal/60 font-light leading-relaxed">{f.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="reviews" data-testid="testimonials-section" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs tracking-[0.4em] uppercase text-gold mb-4">From Our Customers</p>
          <h2 className="font-serif-lux text-4xl sm:text-5xl text-charcoal mb-16">Real recovery stories</h2>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={t.name}
                data-testid={`testimonial-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white/70 backdrop-blur-sm border border-taupe/30 rounded-sm overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={t.image} alt={`${t.name} using the ${t.product}`} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <div className="flex gap-1 mb-4" aria-label={`${t.rating} out of 5 stars`}>
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={14} className={s < t.rating ? "text-gold fill-gold" : "text-taupe"} />
                    ))}
                  </div>
                  <blockquote className="text-sm text-charcoal/75 font-light leading-relaxed flex-1">{t.quote}</blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-taupe/30">
                    <p className="text-sm font-medium text-charcoal">{t.name}</p>
                    <p className="text-[11px] tracking-widest uppercase text-charcoal/50 mt-0.5">{t.location}</p>
                    <p className="text-[11px] text-gold-dark mt-2">{t.product}</p>
                  </figcaption>
                </div>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default TrustSection;
